import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, X } from "lucide-react";
import { PageHead, Reveal } from "@/components/Motion";
import { Kept, Loading, useApi } from "@/components/Common";
import { api, media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const Candle = ({ i }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="flex flex-col items-center">
    <span className="candle-flame block w-2 h-4 rounded-full bg-gradient-to-t from-[#C9A24B] to-[#FFF2C6] shadow-[0_0_14px_4px_rgba(255,200,100,.6)]" />
    <span className="block w-2.5 h-7 bg-[#F6EFE0] rounded-sm mt-0.5" />
  </motion.div>
);

const Initial = ({ name, className = "" }) => (
  <div className={`grid place-items-center bg-[radial-gradient(ellipse_at_center,#163B29,#0B1D14_75%)] ${className}`} data-testid="memorial-initial">
    <div className="grid place-items-center w-28 h-28 rounded-full border border-[#C9A24B]/60 shadow-[0_0_0_10px_rgba(201,162,75,.06)]"><span className="font-display text-5xl text-[#C9A24B]">{(name || "?")[0]}</span></div>
  </div>
);

export default function Memoriam() {
  const { session } = useSite() || {};
  const { data, loading } = useApi("/public/list/memorials", [session?.active]);
  const [open, setOpen] = useState(null);
  const [counts, setCounts] = useState({});
  const items = Array.isArray(data?.items) ? data.items : [];
  const light = async (m) => {
    try { const r = await api.post(`/public/candle/${m.id}`); setCounts((c) => ({ ...c, [m.id]: r.data?.candles ?? 0 })); } catch (e) { /* ignore */ }
  };
  const n = (m) => counts[m.id] ?? m.candles ?? 0;
  return (
    <div data-testid="memoriam-page">
      <PageHead eyebrow="Remember · In Memoriam" title="In Memoriam" line="Those who came before us, always part of our tree." />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        {loading ? <Loading /> : items.length ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((m, k) => (
              <Reveal key={m.id} delay={k * 0.1}>
                <button className="group text-left w-full card-gold overflow-hidden" onClick={() => setOpen(m)} data-testid={`memorial-card-${k}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    {m.photo_url ? <img src={media(m.photo_url)} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-[1600ms]" /> : <Initial name={m.name} className="w-full h-full" />}
                  </div>
                  <div className="p-6">
                    <p className="font-display text-2xl">{m.name}</p>
                    <p className="text-sm text-[#E4C87F]">{m.years}</p>
                    <p className="mt-3 text-sm text-[#F6EFE0]/80">{m.tribute}</p>
                    <p className="mt-4 text-xs text-[#F6EFE0]/65 flex items-center gap-2"><Flame size={12} className="text-[#C9A24B]" /> {n(m)} candles lit</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        ) : <Kept title="Our memorials are kept close" text="Tributes are shared with family only for now." era="silent" />}
      </section>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[85] bg-[#070F0B]/96 overflow-y-auto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9 }} data-lenis-prevent data-testid="memorial-tribute">
            <button className="fixed top-5 right-5 lb-btn z-10" onClick={() => setOpen(null)} data-testid="memorial-close"><X size={18} /></button>
            <div className="max-w-5xl mx-auto px-6 py-24 grid gap-12 md:grid-cols-2 items-center">
              {open.photo_url ? <motion.img initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6 }} src={media(open.photo_url)} alt={open.name} className="w-full aspect-[4/5] object-cover rounded-lg border border-[#C9A24B]/50 grayscale hover:grayscale-0 transition-[filter] duration-1000" /> : <Initial name={open.name} className="w-full aspect-[4/5] rounded-lg border border-[#C9A24B]/50" />}
              <div>
                <p className="font-mono-x text-xs tracking-[0.3em] text-[#C9A24B]">{open.years}</p>
                <h2 className="mt-4 font-display text-5xl">{open.name}</h2>
                <p className="mt-6 font-accent italic text-2xl text-[#E4C87F]">{open.tribute}</p>
                <p className="mt-6 text-[#F6EFE0]/85 leading-relaxed">{open.story}</p>
                <div className="mt-10 flex items-end gap-2 flex-wrap min-h-[48px]" data-testid="candle-row">{Array.from({ length: Math.min(40, n(open)) }, (_, i) => <Candle key={i} i={i} />)}</div>
                <div className="mt-6 flex items-center gap-4">
                  <button className="btn-gold" onClick={() => light(open)} data-testid="light-candle-btn"><Flame size={16} /> Light a candle</button>
                  <span className="text-sm text-[#F6EFE0]/80" data-testid="candle-count">{n(open)} candles</span>
                </div>
                <Link to="/tree" className="sweep mt-8 inline-block text-sm text-[#E4C87F]" data-testid="memorial-tree-link">See their place on the tree</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
